import { ChangeDetectionStrategy, Component, EventEmitter, inject, OnInit, Output } from '@angular/core'
import { MessageService } from 'primeng/api'

import { BAND_UI, CREDIT_SOURCE_LABELS, flagLabel, flagsText, formatCoverage, formatMetricValue, formatScore, fxLegend, METRIC_STATE_UI, RENT_SOURCE_LABELS } from '../risk-scorecard.labels'
import { isLegacyScorecard, LegalGate, MetricResult, PillarResult, RiskCapture, SaveRiskCapture } from '@data/interfaces/risk-scorecard.interface'
import { formatPesos, formatShortDate } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { ApiRiskScorecardService } from '@data/services/modules/risk-scorecard/api-risk-scorecard.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { RiskScorecardTabBase } from '../risk-scorecard-tab.base'

//* Mismo mínimo que la API (`CAPTURE_REASON_MIN_LENGTH`): la captura mueve el puntaje y tiene que quedar por qué.
const REASON_MIN_LENGTH = 10
//* Marcas de la respuesta que ya se pintan como franja propia; no se repiten en «Avisos».
const BANNER_FLAGS = ['umbrales_en_validacion', 'foto_pendiente', 'sin_entidad_syntage', 'foto_reconstruida', 'cuentas_convertidas_a_pesos', 'cuentas_sin_tipo_de_cambio']

/** Lo que se edita en el formulario de captura. */
export interface CaptureDraft {
  withC5: boolean
  clientCv: number | null
  guarantorCv: number | null
  reputation: number | null
  withCollateral: boolean
  solidGuarantee: boolean
  notes: string
  reason: string
}

/**
 * Calculadora de riesgo (plan v4.2 §8 y v4.3 §9.1): banda, puntaje en la escala del Excel (base + colateral), motivos,
 * vetos, señales para Legal, pilares con sus métricas, historial crediticio usado y, en el arrendamiento, la captura de
 * C5 y la garantía sólida. No cambia el Comité; la del cliente decide su preoferta (plan v4.3 §2b). Un sello del motor 1 se pinta con la
 * escala 0-100 de entonces.
 */
@Component({
  selector: 'app-ciec-risk-tab',
  templateUrl: './ciec-risk-tab.component.html',
  styleUrls: ['./ciec-risk-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CiecRiskTabComponent extends RiskScorecardTabBase implements OnInit {
  /** «Ver los estados financieros»: la vista abre esa pestaña. */
  @Output() openTab = new EventEmitter<'statements'>()

  capture: RiskCapture | null = null
  captureHistory: RiskCapture[] = []
  captureLoadFailed = false
  editing = false
  saving = false
  draft: CaptureDraft = emptyDraft()

  readonly bands = BAND_UI
  readonly states = METRIC_STATE_UI
  readonly rentSources = RENT_SOURCE_LABELS
  readonly creditSources = CREDIT_SOURCE_LABELS
  readonly flagLabel = flagLabel
  readonly flagsText = flagsText
  readonly score = formatScore
  readonly coverage = formatCoverage
  readonly pesos = formatPesos
  readonly shortDate = formatShortDate
  readonly fxLegend = fxLegend
  readonly reasonMin = REASON_MIN_LENGTH

  private readonly api = inject(ApiRiskScorecardService)
  private readonly auth = inject(AuthService)
  private readonly messageService = inject(MessageService)
  //* Quien no ve la preoferta tampoco necesita saber dónde vive.
  readonly canViewPreoffer = this.auth.userHasAuthorization('lease-preoffer', 'view')

  override ngOnInit(): void {
    super.ngOnInit()
    if (this.isLease) this.loadCapture()
  }

  get isLease(): boolean {
    return this.context?.scope === 'lease'
  }

  get isCompany(): boolean {
    return this.result?.personType === 'PM'
  }

  /** Sellado con el motor 1 (escala 0-100): se pinta como entonces. */
  get isLegacy(): boolean {
    return isLegacyScorecard(this.result)
  }

  /** Nombres del 2º obligado detectado en los contactos del cliente; vacío si no cuenta. */
  get secondGuarantorNames(): string[] {
    return this.result?.collateral?.items.find((item) => item.code === 'second_guarantor')?.names ?? []
  }

  get secondGuarantorPoints(): number {
    return this.result?.collateral?.items.find((item) => item.code === 'second_guarantor')?.points ?? 0
  }

  get hasSecondGuarantor(): boolean {
    return !!this.result?.collateral?.items.some((item) => item.code === 'second_guarantor')
  }

  get legalGate(): LegalGate | null {
    return this.result?.legalGate ?? null
  }

  /** Lo más que da un pilar: en el motor 2 su máximo en la escala del Excel; en un sello viejo, su peso. */
  pillarMax(pillar: PillarResult): number {
    return pillar.maxPoints ?? pillar.weight
  }

  /** Lo que vale el tramo de la métrica sobre 20 (motor 2); en un sello viejo, el tramo mismo. */
  tierPointsOf(metric: MetricResult): number {
    return metric.tierPoints ?? metric.tier
  }

  /** Congelado o sellado: lo que se vio al aprobar no cambia, tampoco su captura. */
  get isFrozen(): boolean {
    return !!this.context?.frozen || !!this.view?.seal?.sealed
  }

  get canCapture(): boolean {
    return this.isLease && !this.isFrozen && this.auth.userHasAuthorization('risk-scorecard', 'capture')
  }

  /** Avisos del motor y de la respuesta que no tienen franja propia. */
  get notices(): string[] {
    const all = [...(this.result?.flags ?? []), ...(this.view?.flags ?? [])]
    return [...new Set(all)].filter((flag) => !BANNER_FLAGS.includes(flag))
  }

  /**
   * El historial tenía más de 90 días a la fecha del cálculo (R-17) y cuenta como parcial. Lo decide el motor: en un
   * resultado sellado es la fecha de la activación, no la de hoy.
   */
  get historyStale(): boolean {
    return !!this.result?.creditHistory?.stale
  }

  /** Promedio de C5 de la captura vigente, como lo calcula el motor. */
  get c5Average(): number | null {
    const c5 = this.capture?.c5
    return c5 ? (c5.clientCv + c5.guarantorCv + c5.reputation) / 3 : null
  }

  get canSave(): boolean {
    const d = this.draft
    if (this.saving || (!d.withC5 && !d.withCollateral)) return false
    if (d.withC5 && ![d.clientCv, d.guarantorCv, d.reputation].every((value) => typeof value === 'number' && value >= 0 && value <= 100)) return false
    return d.reason.trim().length >= REASON_MIN_LENGTH
  }

  metricValue(metric: MetricResult): string {
    return formatMetricValue(metric.value, metric.unit)
  }

  pillarCoverage(pillar: PillarResult): string {
    return formatCoverage(pillar.coverageBp)
  }

  goToStatements(): void {
    this.openTab.emit('statements')
  }

  startEdit(): void {
    this.draft = draftFrom(this.capture)
    this.editing = true
  }

  cancelEdit(): void {
    this.editing = false
  }

  save(): void {
    if (!this.canSave || !this.context) return
    const d = this.draft
    const body: SaveRiskCapture = {
      c5: d.withC5 ? { clientCv: Number(d.clientCv), guarantorCv: Number(d.guarantorCv), reputation: Number(d.reputation) } : null,
      collateral: d.withCollateral ? { solidGuarantee: d.solidGuarantee, ...(d.notes.trim() ? { notes: d.notes.trim() } : {}) } : null,
      reason: d.reason.trim(),
    }
    this.saving = true
    this.api.saveCapture(this.context.id, body).subscribe((response) => {
      this.saving = false
      if (response.error || !response.data) {
        this.messageService.add({ key: 'risk-scorecard', severity: 'error', summary: 'No se guardó la captura', detail: response.msg })
      } else {
        this.editing = false
        this.messageService.add({ key: 'risk-scorecard', severity: 'success', summary: 'Captura guardada', detail: 'La calculadora se volvió a calcular con ella.' })
        this.loadCapture()
        this.store.reload(this.context!)
      }
      this.cd.markForCheck()
    })
  }

  private loadCapture(): void {
    if (!this.context) return
    this.api.getCapture(this.context.id).subscribe((response) => {
      this.captureLoadFailed = response.error
      this.capture = response.data?.current ?? null
      this.captureHistory = response.data?.history ?? []
      this.cd.markForCheck()
    })
  }
}

function emptyDraft(): CaptureDraft {
  return { withC5: true, clientCv: null, guarantorCv: null, reputation: null, withCollateral: false, solidGuarantee: false, notes: '', reason: '' }
}

//* Parte de lo vigente: cambiar una calificación no obliga a recapturar las demás. El motivo siempre es nuevo.
function draftFrom(capture: RiskCapture | null): CaptureDraft {
  if (!capture) return emptyDraft()
  return {
    withC5: !!capture.c5,
    clientCv: capture.c5?.clientCv ?? null,
    guarantorCv: capture.c5?.guarantorCv ?? null,
    reputation: capture.c5?.reputation ?? null,
    withCollateral: !!capture.collateral,
    solidGuarantee: !!capture.collateral?.solidGuarantee,
    notes: capture.collateral?.notes ?? '',
    reason: '',
  }
}
